import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { BottomSheet, Box, Column, InlineSelect, MenuItem, Row, Typography, WithInfoIcon } from 'seagull';
import { ChevronDown } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useResponsive } from '~/client-flow/contexts';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { useCobConfig } from '../../../../CobConfigContext';
import { BillingSchedule } from '../../../../ReviewServices/ReviewServicesV2/BillingSchedule/BillingSchedule';
import { useOptionalServicesSelection, usePackageSelection } from '../../../hooks';
import styles from './ReviewServices.module.scss';
import { ServiceItem } from './ServiceItem';
export const GetReviewServicesTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const ReviewServices = (param)=>{
    let { testId } = param;
    var _relationship_serviceBundles;
    const { ReviewServicesTestIds } = useMemo(()=>GetReviewServicesTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('clientFlow.reviewServices');
    const { relationship, entityContext } = useCobConfig();
    const { getNow } = useIoContext();
    const { isMobile } = useResponsive();
    const { selectedBundleId, updateSelectedPackage } = usePackageSelection();
    const { toggleOptionalService, isServiceSelected, selectedOptionalServices } = useOptionalServicesSelection();
    const [isPackageSheetOpen, setIsPackageSheetOpen] = useState(false);
    const packages = useMemo(()=>{
        var _ref;
        var _relationship_serviceBundles;
        return (_ref = (_relationship_serviceBundles = relationship.serviceBundles) === null || _relationship_serviceBundles === void 0 ? void 0 : _relationship_serviceBundles.bundles) !== null && _ref !== void 0 ? _ref : [];
    }, [
        (_relationship_serviceBundles = relationship.serviceBundles) === null || _relationship_serviceBundles === void 0 ? void 0 : _relationship_serviceBundles.bundles
    ]);
    const hasMultiplePackages = packages.length > 1;
    const selectedPackage = useMemo(()=>{
        return packages.find((pkg)=>pkg.id === selectedBundleId);
    }, [
        packages,
        selectedBundleId
    ]);
    const [services, nonOptionalServices, optionalServices] = useMemo(()=>{
        const services = relationshipModel.actualServices(relationship, selectedBundleId);
        return [
            services,
            services.filter((s)=>s.participation.type === 'mandatory'),
            services.filter((s)=>s.participation.type === 'optional')
        ];
    }, [
        relationship,
        selectedBundleId
    ]);
    const handlePackageChange = (event)=>{
        updateSelectedPackage(event.target.value);
    };
    const handleMobilePackageSelect = (packageId)=>{
        updateSelectedPackage(packageId);
        setIsPackageSheetOpen(false);
    };
    return /*#__PURE__*/ _jsxs(Column, {
        className: styles.root,
        testId: ReviewServicesTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.scrollableContent,
                children: [
                    hasMultiplePackages && !isMobile && /*#__PURE__*/ _jsx(Box, {
                        className: styles.packageSelector,
                        testId: ReviewServicesTestIds.PackageSelector(),
                        children: /*#__PURE__*/ _jsx(InlineSelect, {
                            value: selectedBundleId !== null && selectedBundleId !== void 0 ? selectedBundleId : '',
                            onChange: handlePackageChange,
                            children: packages.map((pkg)=>/*#__PURE__*/ _jsx(MenuItem, {
                                    value: pkg.id,
                                    children: pkg.name
                                }, pkg.id))
                        })
                    }),
                    hasMultiplePackages && isMobile && /*#__PURE__*/ _jsxs(Row, {
                        gap: 4,
                        alignItems: "center",
                        className: cn(styles.mobilePackageSelector, styles.textOnPrimaryBg),
                        onClick: ()=>setIsPackageSheetOpen(true),
                        testId: ReviewServicesTestIds.PackageSelector(),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h6",
                                weight: "normal",
                                children: selectedPackage === null || selectedPackage === void 0 ? void 0 : selectedPackage.name
                            }),
                            /*#__PURE__*/ _jsx(ChevronDown, {})
                        ]
                    }),
                    !hasMultiplePackages && selectedPackage && /*#__PURE__*/ _jsx(Typography, {
                        variant: "h6",
                        weight: "normal",
                        className: styles.textOnPrimaryBg,
                        children: selectedPackage.name
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        gap: 16,
                        testId: ReviewServicesTestIds.ServicesList(),
                        children: [
                            /*#__PURE__*/ _jsx(Column, {
                                gap: 12,
                                children: nonOptionalServices.map((service, idx)=>/*#__PURE__*/ _jsx(ServiceItem, {
                                        service: service,
                                        relationship: relationship,
                                        now: getNow(),
                                        testId: "service-item-".concat(idx)
                                    }, service.id))
                            }),
                            optionalServices.length > 0 && /*#__PURE__*/ _jsxs(Column, {
                                gap: 12,
                                children: [
                                    /*#__PURE__*/ _jsx(WithInfoIcon, {
                                        className: styles.textOnPrimaryBg,
                                        info: texts.optionalServicesInfo(),
                                        testId: ReviewServicesTestIds.OptionalServicesHeader(),
                                        children: /*#__PURE__*/ _jsx(Typography, {
                                            children: texts.additionalServicesToChooseFrom()
                                        })
                                    }),
                                    optionalServices.map((service, idx)=>/*#__PURE__*/ _jsx(ServiceItem, {
                                            service: service,
                                            relationship: relationship,
                                            now: getNow(),
                                            isOptional: true,
                                            isSelected: isServiceSelected(service.id),
                                            onToggle: ()=>toggleOptionalService(service.id),
                                            testId: "service-item-optional-".concat(idx)
                                        }, service.id))
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.billingScheduleSection,
                        testId: ReviewServicesTestIds.BillingSchedule(),
                        children: /*#__PURE__*/ _jsx(BillingSchedule, {
                            services: services,
                            serviceBundles: relationship.serviceBundles,
                            effectiveDate: relationship.effectiveDate,
                            selectedOptionalServices: selectedOptionalServices,
                            selectedBundleId: selectedBundleId,
                            entityContext: entityContext
                        })
                    })
                ]
            }),
            isMobile && hasMultiplePackages && /*#__PURE__*/ _jsx(BottomSheet, {
                open: isPackageSheetOpen,
                onClose: ()=>setIsPackageSheetOpen(false),
                variant: "branded",
                children: /*#__PURE__*/ _jsxs(Column, {
                    gap: 16,
                    className: styles.packageSheetContent,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "normal",
                            className: styles.packageSheetTitle,
                            children: texts.selectAPackage()
                        }),
                        /*#__PURE__*/ _jsx(Column, {
                            className: styles.packageList,
                            children: packages.map((pkg)=>/*#__PURE__*/ _jsx(MenuItem, {
                                    selected: pkg.id === selectedBundleId,
                                    onClick: ()=>handleMobilePackageSelect(pkg.id),
                                    children: pkg.name
                                }, pkg.id))
                        })
                    ]
                })
            })
        ]
    });
};
